import { useEffect, useState } from 'react';
import { UseFormSetValue } from 'react-hook-form';
import { OnSelectChange, SelectItemProps } from '@ui/src';
import { useLazyGetCustomersSicCodesQuery } from '../../../../store/dictionaries/services';
import useGetAutocompleteList from '../../../../store/dictionaries/helpers/use-sic-codes-autocomplete';
import { CorporateFormValues } from '../../../../hoc/new-application/types';

const useSicCodes = ({
  values,
  index,
  setValue,
  setValuesList,
  value,
}: {
  values: {
    name: string;
    id: string;
  }[];
  index: number;
  setValue: UseFormSetValue<CorporateFormValues>;
  setValuesList: (
    data: {
      name: string;
      id: string;
    }[],
  ) => void;
  value: {
    name: string;
    id: string;
  };
}) => {
  const [sicCodeErrorVisible, setSicCodeErrorVisible] = useState(false);
  const [nextPageIsLoading, setNextPageIsLoading] = useState(false);

  const {
    onSearch,
    isLoading,
    pagination,
    searchQuery,
    setAutocompleteList,
    autocompleteList,
    setPagination,
  } = useGetAutocompleteList();

  const [getList] = useLazyGetCustomersSicCodesQuery();

  const onChange = (e: OnSelectChange) => {
    if (!values.find((item) => item.id === e.target.value)) {
      const nextValues = values.map((item, valueIndex) => {
        const result = { ...item };

        if (index === valueIndex) {
          result.name = e.target.name;
          result.id = e.target.value as string;
        }

        return result;
      });

      setValue(
        'sicCodes',
        nextValues.map((item) => item.id),
      );
      setValuesList(nextValues);
      setSicCodeErrorVisible(false);
    } else {
      setSicCodeErrorVisible(true);
    }
  };

  const onInfinateScroll = async () => {
    if (!nextPageIsLoading) {
      setNextPageIsLoading(true);

      const response = await getList(
        `?take=40&cursor=${pagination?.endCursor}${searchQuery || ''}`,
      );

      if (response && response.data && response.data.data) {
        const nextAutocompleteList = ([] as SelectItemProps[]).concat(
          autocompleteList,
        );

        response.data.data.edges?.forEach((item) => {
          nextAutocompleteList.push({
            name: item.node.code,
            value: item.node.code,
            description: item.node.description,
            suffix: `Risk score: ${item.node.riskScore}`,
          });
        });

        setAutocompleteList(nextAutocompleteList);
        setPagination(response.data.data?.pageInfo);

        setTimeout(() => {
          setNextPageIsLoading(false);
        }, 100);
      }
    }
  };

  const deleteSicCode = (id: string) => {
    let nextValues;

    if (id) {
      nextValues = values.filter((item) => item.id !== id);
    } else {
      nextValues = values.filter((item, itemIndex) => index !== itemIndex);
    }

    setValue(
      'sicCodes',
      nextValues.map((item) => item.id),
    );

    setValuesList(nextValues);
  };

  useEffect(() => {
    if (
      !autocompleteList.find((item) => item.value === value.id) &&
      value.name &&
      !value.name.includes('sicCodes')
    ) {
      onSearch(value.name, true);
    }
  }, [value, autocompleteList]);

  return {
    onChange,
    isLoading,
    onSearch,
    autocompleteList,
    onInfinateScroll,
    pagination,
    nextPageIsLoading,
    deleteSicCode,
    sicCodeErrorVisible,
  };
};

export default useSicCodes;
